import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { Tarefa, Departamento } from '../../types';
import { toast } from 'react-toastify';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Chip,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { tarefaDepartamentoService, TarefaDepartamentoFiltros } from '../../services/tarefaDepartamentoService';
import SkeletonLoader from '../common/SkeletonLoader';
import { obterCorPrioridade } from '../../utils/coresPrioridade';

// Componente para formatar a prioridade com cores
const PrioridadeChip: React.FC<{ prioridade: string }> = ({ prioridade }) => {
  // Mesma paleta de prioridade do resto do sistema — antes usava as cores
  // genéricas success/info/warning/error do MUI, diferentes da cor real
  // usada pro Kanban/modal de tarefa pra essa mesma prioridade.
  const cor = obterCorPrioridade(prioridade);
  return (
    <Chip
      label={prioridade.charAt(0).toUpperCase() + prioridade.slice(1)}
      size="small"
      variant="outlined"
      sx={{ color: cor, borderColor: cor }}
    />
  );
};

// Componente para formatar o status com cores
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getColor = () => {
    switch (status.toLowerCase()) {
      case 'pendente': return 'default';
      case 'em andamento': return 'primary';
      case 'aguardando aprovação': return 'warning';
      case 'concluída': return 'success';
      case 'finalizada': return 'success';
      default: return 'default';
    }
  };

  return (
    <Chip 
      label={status} 
      color={getColor() as any} 
      size="small" 
    />
  );
};

const TarefasAtivasDepartamento: React.FC = () => {
  const { usuario } = useAuth();
  const { departamentos, departamentosLiderados: depLideradosIds, ehLiderDepartamento } = useDepartamento();
  const navigate = useNavigate();
  
  // Estados para controle de dados
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [departamentosLiderados, setDepartamentosLiderados] = useState<Departamento[]>([]);
  const [carregandoDepartamentos, setCarregandoDepartamentos] = useState(false);
  
  // Estados para os filtros de busca
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [tarefasFiltradas, setTarefasFiltradas] = useState<Tarefa[]>([]);
  const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState(false);
  const [departamentoId, setDepartamentoId] = useState<string | null>(null);

  // Carregar tarefas ativas do departamento
  const carregarTarefasAtivasDepartamento = async (pagina: number = 1) => {
    try {
      setCarregando(true);
      setErro(null);
      
      // Verificar se temos um departamento selecionado
      if (!departamentoId && usuario?.tipo === 'admin') {
        console.warn('Admin sem departamento selecionado');
        // Para admin, precisamos de um departamento selecionado
        if (departamentos.length > 0) {
          // Selecionar o primeiro departamento disponível
          setDepartamentoId(departamentos[0].id);
          return; // O useEffect vai chamar esta função novamente quando departamentoId mudar
        } else {
          setErro('Selecione um departamento para visualizar as tarefas');
          setCarregando(false);
          return;
        }
      }
      
      // Preparar filtros para a API
      const filtros: TarefaDepartamentoFiltros = {
        pagina,
        limite: 20,
        status: filtroStatus || undefined,
        prioridade: filtroPrioridade || undefined,
        responsavel: filtroResponsavel || undefined,
        dataInicio: filtroDataInicio || undefined,
        dataFim: filtroDataFim || undefined
      };
      
      // Se o usuário for admin, precisa especificar o departamento
      if (usuario?.tipo === 'admin' && departamentoId) {
        filtros.departamento = departamentoId;
      }
      
      console.log('Carregando tarefas com filtros:', filtros);
      
      const response = await tarefaDepartamentoService.listarTarefasAtivasDepartamento(filtros);
      
      if (response.success) {
        // Mapear as tarefas para garantir que as datas estejam corretas
        const tarefasMapeadas = (response.tarefas || []).map((tarefa: any) => ({
          ...tarefa,
          dataCriacao: tarefa.data_criacao || tarefa.dataCriacao,
          dataVencimento: tarefa.data_vencimento || tarefa.dataVencimento,
          responsavel: tarefa.responsavel || {
            id: tarefa.responsavel_id,
            nome: tarefa.responsavel_nome || 'Não definido',
            email: tarefa.responsavel_email || ''
          },
          criador: tarefa.criador || {
            id: tarefa.criador_id,
            nome: tarefa.criador_nome || 'Não definido',
            email: tarefa.criador_email || ''
          }
        }));
        
        setTarefas(tarefasMapeadas);
        setPaginaAtual(response.paginacao?.pagina || 1);
        setTotalPaginas(response.paginacao?.totalPaginas || 1);
        setTotalRegistros(response.paginacao?.total || 0);
      } else {
        console.error('Resposta da API com erro:', response);
        setErro(response.message || 'Erro ao carregar tarefas ativas do departamento');
        // Inicializar com arrays vazios para evitar erros de renderização
        setTarefas([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar tarefas ativas do departamento:', error);
      setErro(error?.message || 'Erro ao carregar tarefas ativas do departamento');
      // Inicializar com arrays vazios para evitar erros de renderização
      setTarefas([]);
    } finally {
      setCarregando(false);
    }
  };
  
  // Carregar departamentos liderados pelo usuário
  useEffect(() => {
    try {
      if (usuario && ehLiderDepartamento && departamentos.length > 0) {
        setCarregandoDepartamentos(true);
        
        // Filtrar os departamentos que o usuário lidera
        // Usar um array vazio como fallback para depLideradosIds
        const lideradosIds = depLideradosIds || [];
        const depsLiderados = departamentos.filter(dep => lideradosIds.includes(dep.id));
        setDepartamentosLiderados(depsLiderados);
        
        // Se o usuário é líder de pelo menos um departamento, selecionar o primeiro como padrão
        if (depsLiderados.length > 0) {
          setDepartamentoId(depsLiderados[0].id);
        } else if (usuario.departamentoId) {
          // Caso não tenha departamentos liderados, usar o departamento do usuário
          setDepartamentoId(usuario.departamentoId);
        }
      } else if (usuario && usuario.departamentoId) {
        // Se não for líder, usar o departamento do usuário
        setDepartamentoId(usuario.departamentoId);
      } else if (usuario && usuario.tipo === 'admin' && departamentos.length > 0) {
        // Para administradores sem departamento, selecionar o primeiro departamento disponível
        setDepartamentoId(departamentos[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      setErro('Erro ao carregar departamentos. Por favor, recarregue a página.');
    } finally {
      setCarregandoDepartamentos(false);
    }
  }, [usuario, ehLiderDepartamento, departamentos, depLideradosIds]);
  
  // Carregar tarefas quando o departamento selecionado mudar
  useEffect(() => {
    if (departamentoId) {
      carregarTarefasAtivasDepartamento(1);
    }
  }, [departamentoId]);
  
  // Efeito para filtrar as tarefas quando os filtros ou as tarefas mudam
  useEffect(() => {
    filtrarTarefas();
  }, [tarefas, busca]);
  
  // Função para filtrar as tarefas com base nos critérios de busca local
  const filtrarTarefas = () => {
    let resultado = [...tarefas];
    
    // Filtrar por texto de busca (título ou descrição)
    if (busca) {
      const termoBusca = busca.toLowerCase();
      resultado = resultado.filter(tarefa => 
        tarefa.titulo.toLowerCase().includes(termoBusca) || 
        (tarefa.descricao && tarefa.descricao.toLowerCase().includes(termoBusca))
      );
    }
    
    setTarefasFiltradas(resultado);
  };
  
  // Aplicar filtros na API
  const aplicarFiltros = () => {
    carregarTarefasAtivasDepartamento(1);
  };
  
  // Handlers para os campos de filtro
  const handleBuscaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusca(e.target.value);
  };
  
  const handleStatusChange = (e: SelectChangeEvent) => {
    setFiltroStatus(e.target.value);
  };
  
  const handlePrioridadeChange = (e: SelectChangeEvent) => {
    setFiltroPrioridade(e.target.value);
  };
  
  const handleResponsavelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroResponsavel(e.target.value);
  };
  
  const handleDataInicioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroDataInicio(e.target.value);
  };
  
  const handleDataFimChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroDataFim(e.target.value);
  };
  
  const toggleFiltrosAvancados = () => {
    setMostrarFiltrosAvancados(!mostrarFiltrosAvancados);
  };
  
  const limparFiltros = () => {
    setBusca('');
    setFiltroStatus('');
    setFiltroPrioridade('');
    setFiltroResponsavel('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
    
    // Recarregar tarefas sem filtros
    carregarTarefasAtivasDepartamento(1);
  };

  const handleChangePagina = (event: React.ChangeEvent<unknown>, value: number) => {
    carregarTarefasAtivasDepartamento(value);
  };
  
  // Handler para mudança de departamento selecionado
  const handleDepartamentoChange = (e: SelectChangeEvent) => {
    setDepartamentoId(e.target.value);
  };

  const handleVerDetalhes = (tarefaId: string) => {
    navigate(`/tarefas/${tarefaId}`);
  };
  
  const handleExportarCSV = async () => {
    try {
      // Verificar se temos um departamento selecionado
      if (!departamentoId && usuario?.tipo === 'admin') {
        toast.error('Selecione um departamento para exportar as tarefas');
        return;
      }

      toast.info('Preparando exportação...', { autoClose: false, toastId: 'exportando' });
      
      // Preparar filtros para a exportação
      const filtros: TarefaDepartamentoFiltros = {
        status: filtroStatus || undefined,
        prioridade: filtroPrioridade || undefined,
        responsavel: filtroResponsavel || undefined,
        dataInicio: filtroDataInicio || undefined,
        dataFim: filtroDataFim || undefined
      };
      
      // Se o usuário for admin, precisa especificar o departamento
      if (usuario?.tipo === 'admin' && departamentoId) {
        filtros.departamento = departamentoId;
      }
      
      console.log('Exportando tarefas com filtros:', filtros);
      
      await tarefaDepartamentoService.exportarTarefasAtivasDepartamento(filtros);
      
      // Fechar o toast de "preparando exportação"
      toast.dismiss('exportando');
      toast.success('Exportação concluída com sucesso!');
    } catch (error: any) {
      // Fechar o toast de "preparando exportação" se ainda estiver aberto
      toast.dismiss('exportando');
      
      console.error('Erro ao exportar tarefas ativas do departamento:', error);
      toast.error(error?.message || 'Erro ao exportar tarefas ativas do departamento');
    }
  };

  const formatarData = (dataString: string | null) => {
    if (!dataString) return '-';
    try {
      return format(new Date(dataString), 'dd/MM/yyyy');
    } catch (error) {
      return '-';
    }
  };

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {erro}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Tarefas Ativas do Departamento
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportarCSV}
        >
          Exportar CSV
        </Button>
      </Box>
      
      {/* Seletor de departamento para líderes de múltiplos departamentos */}
      {ehLiderDepartamento && departamentosLiderados.length > 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="departamento-select-label">Selecione o Departamento</InputLabel>
            <Select
              labelId="departamento-select-label"
              value={departamentoId || ''}
              label="Selecione o Departamento"
              onChange={handleDepartamentoChange}
              disabled={carregandoDepartamentos}
            >
              {departamentosLiderados.map((dep) => (
                <MenuItem key={dep.id} value={dep.id}>
                  {dep.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}
      
      {/* Campo de busca e filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Buscar por título ou descrição"
                variant="outlined"
                size="small"
                value={busca}
                onChange={handleBuscaChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' }, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                startIcon={<FilterListIcon />}
                onClick={toggleFiltrosAvancados}
                sx={{ mr: 1 }}
              >
                {mostrarFiltrosAvancados ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </Button>
              {mostrarFiltrosAvancados && (
                <Button 
                  variant="text" 
                  onClick={limparFiltros}
                >
                  Limpar Filtros
                </Button>
              )}
            </Box>
          </Box>
          
          {/* Filtros avançados */}
          {mostrarFiltrosAvancados && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(33% - 8px)' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="filtro-status-label">Status</InputLabel>
                    <Select
                      labelId="filtro-status-label"
                      value={filtroStatus}
                      label="Status"
                      onChange={handleStatusChange}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="Pendente">Pendente</MenuItem>
                      <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(33% - 8px)' } }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="filtro-prioridade-label">Prioridade</InputLabel>
                    <Select
                      labelId="filtro-prioridade-label"
                      value={filtroPrioridade}
                      label="Prioridade"
                      onChange={handlePrioridadeChange}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      <MenuItem value="Baixa">Baixa</MenuItem>
                      <MenuItem value="Media">Média</MenuItem>
                      <MenuItem value="Alta">Alta</MenuItem>
                      <MenuItem value="Urgente">Urgente</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(33% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="Responsável"
                    variant="outlined"
                    size="small"
                    value={filtroResponsavel}
                    onChange={handleResponsavelChange}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="Data de criação (início)"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={filtroDataInicio}
                    onChange={handleDataInicioChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="Data de criação (fim)"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={filtroDataFim}
                    onChange={handleDataFimChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={aplicarFiltros}
                >
                  Aplicar Filtros
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
      
      {tarefas.length === 0 ? (
        <Alert severity="info">
          Não há tarefas ativas no departamento.
        </Alert>
      ) : tarefasFiltradas.length === 0 && busca ? (
        <Alert severity="info">
          Nenhuma tarefa corresponde aos filtros aplicados.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Criador</TableCell>
                  <TableCell>Data Criação</TableCell>
                  <TableCell>Data Vencimento</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(busca ? tarefasFiltradas : tarefas).map((tarefa) => (
                  <TableRow key={tarefa.id}>
                    <TableCell>{tarefa.titulo}</TableCell>
                    <TableCell>
                      <StatusChip status={tarefa.status} />
                    </TableCell>
                    <TableCell>
                      <PrioridadeChip prioridade={tarefa.prioridade} />
                    </TableCell>
                    <TableCell>{tarefa.responsavel?.nome || 'Não definido'}</TableCell>
                    <TableCell>{tarefa.criador?.nome || 'Não definido'}</TableCell>
                    <TableCell>{formatarData(tarefa.dataCriacao)}</TableCell>
                    <TableCell>{formatarData(tarefa.dataVencimento)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton 
                          size="small" 
                          onClick={() => handleVerDetalhes(tarefa.id)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {totalRegistros} tarefas
            </Typography>
            <Pagination 
              count={totalPaginas} 
              page={paginaAtual} 
              onChange={handleChangePagina} 
              color="primary" 
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default TarefasAtivasDepartamento;
